import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Button, Select, Switch } from "@/core/ui";
import { useTenant } from "@/core/tenant/TenantProvider";
import { extractRules } from "@/core/puzzles/PuzzlePreviewPage";
import { getPuzzleType } from "@/puzzle-types/registry";
import { findAttempt, useAttempt, useMyAttempts } from "@/core/play/api";
import { loadOptions, saveOptions, type Paper, type SheetOptions } from "./options";
import { PuzzleSheet } from "./PuzzleSheet";

/**
 * `/puzzles/:slug/sheet` — the PDF sheet of a revealed puzzle, with the choices that shape it.
 *
 * Outside the application shell on purpose: this route *is* the document. Saving it as a PDF
 * goes through the browser's own print engine — vector grid, embedded fonts, selectable text —
 * which no client-side PDF library matches, and which needs no dependency at all.
 *
 * The definition comes from the player's own attempt, so the sheet exists only once the clock
 * has started (CLAUDE.md §10): a sheet before the reveal would be the puzzle off the clock.
 */
export function SheetPage() {
  const { slug = "" } = useParams();
  const { t, i18n } = useTranslation();
  const tenant = useTenant();
  const [params] = useSearchParams();
  // `?team=` names the participant, as on the play page; a team the caller is not part of
  // simply has no attempt to print.
  const teamId = Number(params.get("team")) || null;
  const attempts = useMyAttempts();
  const existing = findAttempt(attempts.data?.[slug], teamId);
  const attempt = useAttempt(existing?.id ?? null);
  const [options, setOptions] = useState<SheetOptions>(loadOptions);
  const payload = attempt.data ?? null;
  const title = payload?.puzzle.title;

  useEffect(() => saveOptions(options), [options]);

  // The browser names the PDF after the document, so the document is named after the puzzle.
  useEffect(() => {
    if (!title) return;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);

  if (attempts.isPending || (existing && attempt.isPending))
    return <p className="bt-page-message">{t("app.loading")}</p>;
  if (attempts.isError || attempt.isError)
    return <Alert tone="danger">{t("app.error_generic")}</Alert>;
  if (!existing || !payload) {
    return (
      <div className="bt-sheet-desk">
        <div className="bt-sheet-message">
          <p>{t("sheet.not_revealed")}</p>
          <Link to={`/puzzles/${slug}`}>{t("sheet.open_puzzle")}</Link>
        </div>
      </div>
    );
  }
  if (!getPuzzleType(payload.puzzle.puzzle_type)?.PrintView) {
    return (
      <div className="bt-sheet-desk">
        <div className="bt-sheet-message">
          <p>{t("sheet.unavailable")}</p>
          <Link to={`/puzzles/${slug}`}>{t("sheet.open_puzzle")}</Link>
        </div>
      </div>
    );
  }

  const update = (patch: Partial<SheetOptions>) => setOptions((prev) => ({ ...prev, ...patch }));
  return (
    <div className="bt-sheet-desk">
      <div className="bt-sheet-toolbar">
        <Link to={`/puzzles/${slug}`} className="bt-sheet-toolbar__back">
          {t("sheet.open_puzzle")}
        </Link>
        <label className="bt-sheet-toolbar__option">
          {t("sheet.paper")}
          <Select
            className="bt-sheet-toolbar__select"
            value={options.paper}
            onChange={(event) => update({ paper: event.target.value as Paper })}
          >
            <option value="a4">{t("sheet.paper_a4")}</option>
            <option value="letter">{t("sheet.paper_letter")}</option>
          </Select>
        </label>
        <Switch
          id="sheet-rules"
          checked={options.rules}
          onChange={(rules) => update({ rules })}
          label={t("sheet.with_rules")}
        />
        <Switch
          id="sheet-second-copy"
          checked={options.secondCopy}
          onChange={(secondCopy) => update({ secondCopy })}
          label={t("sheet.second_copy")}
        />
        <Switch
          id="sheet-qr"
          checked={options.qr}
          onChange={(qr) => update({ qr })}
          label={t("sheet.qr")}
        />
        <Button className="bt-sheet-toolbar__save" onClick={() => window.print()}>
          {t("sheet.save")}
        </Button>
      </div>
      <p className="bt-sheet-toolbar__hint">{t("sheet.save_hint")}</p>
      <PuzzleSheet
        options={options}
        source={{
          slug,
          puzzleType: payload.puzzle.puzzle_type,
          title: payload.puzzle.title,
          author: payload.puzzle.author,
          difficulty: payload.puzzle.difficulty,
          publishedAt: payload.puzzle.published_at,
          credit: payload.credit,
          definition: payload.definition,
          rules: extractRules(payload.definition, i18n.language),
          address: `${window.location.host}/puzzles/${slug}`,
          // A camera cannot guess a scheme, and a team sheet leads back to the team's own
          // attempt through the same `?team=` the play page reads.
          url: `${window.location.origin}/puzzles/${slug}${teamId ? `?team=${teamId}` : ""}`,
          publisher: tenant.branding.display_name || tenant.name,
        }}
      />
    </div>
  );
}
